import React, { useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useI18next } from "gatsby-plugin-react-i18next";
import classNames from "classnames";

import { commaDelineation } from "../utils/commaDelineation";
import { useApplyAfterWidth } from "../utils/hooks";

import TsxLogo from "../assets/new/Sidebar_desktop_TSX_logo_green.svg";
import OtcLogo from "../assets/new/Sidebar_desktop_OTC_Logo_green.svg";
import CmeLogo from "../assets/new/Sidebar_desktop_CME_Logo_Green.svg";
import LinkedInLogo from "../assets/page/linkedin_blue.svg";
import TwitterLogo from "../assets/page/twitter_blue.svg";
import YouTubeLogo from "../assets/page/youtube_blue.svg";
import FacebookLogo from "../assets/page/facebook_blue.svg";

const STOCKS_DATA_EXPIRATION_MINUTES = 15;

const SocialLinkItem = ({ logo, link }) => {
  return (
    <div className="flex flex-col items-center my-12px hover:opacity-75">
      <a href={link} target="_blank" rel="noopener noreferrer" className="pointer">
        <img className="w-24px h-24px" src={logo} alt="Social" />
      </a>
    </div>
  );
};

const SocialLinks = ({ socialLinks }) => {
  return (
    <div className="flex flex-col items-center justify-between">
      <SocialLinkItem logo={LinkedInLogo} link={socialLinks.linkedInLink} />
      <SocialLinkItem logo={TwitterLogo} link={socialLinks.twitterLink} />
      <SocialLinkItem logo={YouTubeLogo} link={socialLinks.youTubeLink} />
      <SocialLinkItem logo={FacebookLogo} link={socialLinks.facebookLink} />
    </div>
  );
};

const StockInfoItem = ({ stockLabel, price, logo, iconClassName }) => {
  return (
    <div
      className={classNames(
        "relative flex flex-col items-center justify-between my-28px",
        "after:content-[''] after:block after:absolute after:top-[calc(100%+1.75rem)] after:w-50px after:h-2px after:bg-blue",
        "last:after:hidden transition-opacity duration-1000",
      )}
    >
      <img src={logo} alt={"Stock Logo"} className={classNames("w-70px", iconClassName)} />
      <span className="block my-12px !text-10px">{stockLabel}</span>
      <span className="!text-10px">${price}</span>
    </div>
  );
};

const StockInfo = ({ stockItem, spotGoldLabel, stock1Label, stock2Label }) => {
  if (!stockItem) {
    return null;
  }

  return (
    <div className="relative top-90px flex flex-col items-center justify-center">
      <StockInfoItem stockLabel={stock1Label} price={stockItem.strockGminPrice} logo={TsxLogo} iconClassName="h-63px" />
      <StockInfoItem stockLabel={stock2Label} price={stockItem.strockOtcPrice} logo={OtcLogo} iconClassName="h-15px" />
      <StockInfoItem stockLabel={spotGoldLabel} price={stockItem.spotGold} logo={CmeLogo} iconClassName="h-11px" />
    </div>
  );
};

const StockBar = () => {
  const { language } = useI18next();
  const query = useStaticQuery(graphql`
    query StocksQuery {
      data: allContentfulHeaderAndFooter {
        nodes {
          stock1Label
          stock2Label
          stocksSpotGoldLabel
          youTubeLink
          twitterLink
          linkedInLink
          facebookLink
          node_locale
        }
      }
    }
  `);

  function getFromLocalStorage(key) {
    if (typeof window !== "undefined") {
      const itemStr = localStorage.getItem(key);

      if (!itemStr) {
        return null;
      }

      const item = JSON.parse(itemStr);
      const now = new Date();

      if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return item.value;
    }

    return null;
  }

  const [stockItem, setStockItem] = useState(getFromLocalStorage("stockData"));
  const isDesktop = useApplyAfterWidth(1023);

  const headerData = query?.data?.nodes.filter((data) => data.node_locale === language)[0];

  const socialLinks = {
    socialsTitle: query?.data?.nodes?.[0].socialsTitle,
    linkedInLink: query?.data?.nodes?.[0]?.linkedInLink,
    twitterLink: query?.data?.nodes?.[0]?.twitterLink,
    youTubeLink: query?.data?.nodes?.[0]?.youTubeLink,
    facebookLink: query?.data?.nodes?.[0]?.facebookLink,
  };

  useEffect(() => {
    async function fetchData() {
      function saveToLocalStorage(key, value, ttl) {
        const now = new Date();
        const item = {
          value: value,
          expiry: now.getTime() + ttl,
        };
        localStorage.setItem(key, JSON.stringify(item));
      }

      const storedData = getFromLocalStorage("stockData");

      if (storedData) {
        setStockItem(storedData);
      } else {
        const reqs = [
          fetch(
            `${process.env.GATSBY_STOCK_TRACK_URL}&symbol=${process.env.GATSBY_STOCK_SYMBOL}&apikey=${process.env.GATSBY_STOCK_API_KEY}`,
            {
              headers: { "Content-Type": "application/json" },
            },
          )
            .then((re) => re.json())
            .then((res) => res["Global Quote"])
            .catch((err) => console.log("Stock fetch error: ", err)),

          fetch(
            `${process.env.GATSBY_STOCK_TRACK_URL}&symbol=${process.env.GATSBY_STOCK_OTC_SYMBOL}&apikey=${process.env.GATSBY_STOCK_API_KEY}`,
            {
              headers: { "Content-Type": "application/json" },
            },
          )
            .then((re) => re.json())
            .then((res) => res["Global Quote"])
            .catch((err) => console.log("Stock OTC fetch error: ", err)),

          fetch(process.env.GATSBY_GOLD_API_URL, {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": process.env.GATSBY_GOLD_API_KEY,
            },
          })
            .then((re) => re.json())
            .then((res) => res["ask"])
            .catch((err) => console.log("Spot fetch error: ", err)),
        ];

        Promise.all(reqs)
          .then((result) => {
            const [stock, stockOtc, spot] = result;

            const newData = {
              strockGminPrice: stock ? `${Number(stock["05. price"]).toFixed(2)} CAD` : "0",
              strockOtcPrice: stockOtc ? `${Number(stockOtc["05. price"]).toFixed(2)} USD` : "0",
              spotGold: `${commaDelineation(Number(spot).toFixed(2))} USD`,
            };

            setStockItem(newData);
            saveToLocalStorage("stockData", newData, STOCKS_DATA_EXPIRATION_MINUTES * 60 * 1000);
          })
          .catch((err) => console.log("Stock data fetch error: ", err));
      }
    }

    if (isDesktop) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  return (
    <div
      id="stock_bar"
      className={classNames(
        "h-full box-border w-[var(--stock-bar-width)] hidden lg:flex flex-col items-center justify-between text-18px font-lato",
        "fixed left-[0] py-18px z-30",
        "bg-light-gray",
      )}
    >
      <div>
        <StockInfo
          stockItem={stockItem}
          stock1Label={headerData.stock1Label}
          stock2Label={headerData.stock2Label}
          lastPriceLabel={headerData.stocksLastPriceLabel}
          spotGoldLabel={headerData.stocksSpotGoldLabel}
        />
      </div>
      <SocialLinks socialLinks={socialLinks} />
    </div>
  );
};
export default StockBar;
